import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AuthgardAdminAction, AuthgardMfaStatus, UserType } from "src/common/enums";
import { humanizeAdminErrorCode, normalizeMfaStatus } from "src/common/util";
import { executeAuthgardAdminOperation } from "src/components/auth/authgard-admin-provider";
import { getMfaStatuses } from "src/components/auth/mfa-status-provider";
import { AuthgardResultStatus } from "src/models/authgard-item-result";
import { logger } from "src/logger";

export type MfaModalProvider = 'SCAN2AUTH' | 'FIDO2';

// Invite fields (expiry + registration code) only exist while an enrollment is active
// (INITIATED / REINITIATED / RESET_FIDO2 / REREGISTER). `invite: null` is the terminal case (NO_RECORD /
// ONBOARDED), which makes "an expiry or code on a terminal status" unrepresentable. The inner
// fields stay individually nullable to mirror the backend result (a status can arrive before its
// code is surfaced, and the code is masked until an explicit reveal).
interface Invite {
    expiry: Date | null;
    regCode: string | null;
}

interface Enrollment {
    status: AuthgardMfaStatus;
    invite: Invite | null;
}

// The read (bulkGetMfaStatus) hands back the invite expiry as an ISO-8601 string (or null). Parse
// it to epoch millis once: the open effect can then depend on a stable primitive instead of a Date
// identity that changes every render, and a malformed value degrades to "no expiry" rather than an
// Invalid Date.
const parseExpiryMs = (iso?: string | null): number | null => {
    if (!iso) {
        return null;
    }
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
};

export interface UseManageScan2AuthArgs {
    open: boolean;
    principalArn: string;
    // Scope (site) and userType are required by bulkExecuteAuthgardAdminOperation's target input.
    scope: string;
    userType: string;
    mfaProvider: MfaModalProvider;
    initialStatus: AuthgardMfaStatus;
    // Raw ISO-8601 invite expiry from bulkGetMfaStatus (or null when there's no pending invite).
    // Seeds the invite's expiry directly — the hook no longer fabricates one.
    initialExpiry?: string | null;
}

export interface UseManageScan2AuthResult {
    // state (currentStatus / expiryDate / regCode are derived projections of the internal
    // Enrollment — the render reads them as before, but illegal combinations can't be constructed)
    currentStatus: AuthgardMfaStatus;
    expiryDate: Date | null;
    regCode: string | null;
    revealed: boolean;
    errorMessage: string | null;
    submitting: boolean;
    // True while handleRefresh is re-fetching this principal's live status.
    refreshing: boolean;
    // derived (isExpired is intentionally NOT exposed — it's purely presentational and the render
    // derives it from expiryDate, so keeping it here would be a redundant second source of truth)
    hasActiveEnrollment: boolean;
    // actions (the confirm/keep toggle lives in the ConfirmableAction UI component, not here)
    handleGenerateCode: () => void;
    handleRevealCode: () => void;
    cancelEnrollment: () => Promise<boolean>;
    revoke: () => Promise<boolean>;
    handleRefresh: () => Promise<void>;
    resetState: () => void;
}

/**
 * Owns all state + admin-operation orchestration for the single-principal Authgard MFA
 * management modal (Scan2Auth / FIDO2). The modal itself becomes presentational: it renders this
 * hook's returned state and wires the returned actions to buttons. Every admin operation is
 * authorized server-side by PCS/Authgard — this hook never gates authorization.
 */
export const useManageScan2Auth = (args: UseManageScan2AuthArgs): UseManageScan2AuthResult => {
    const { open, principalArn, scope, userType, mfaProvider, initialStatus, initialExpiry } = args;
    // Parse the ISO expiry to a stable primitive (see parseExpiryMs) so the open effect below keys
    // off the millis, not a fresh Date instance each render.
    const initialExpiryMs = parseExpiryMs(initialExpiry);

    const { t } = useTranslation();

    // Domain state: the MFA status plus (only when active) its invite. Server-authoritative.
    const [enrollment, setEnrollment] = useState<Enrollment>({
        status: AuthgardMfaStatus.NO_RECORD,
        invite: null,
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Guards against concurrent/double submits of an admin operation. The ref is the source of
    // truth for the guard (synchronous); `submitting` drives the disabled state on the buttons.
    const [submitting, setSubmitting] = useState<boolean>(false);
    const submittingRef = useRef<boolean>(false);
    // Drives the disabled state on the Refresh button while a live-status re-fetch is in flight.
    const [refreshing, setRefreshing] = useState<boolean>(false);
    // Monotonic id tagging each refresh, so a request that resolves after the modal is reset/reopened
    // (or after a newer refresh starts) can be discarded instead of clobbering the re-seeded state.
    const refreshIdRef = useRef<number>(0);

    // Flat projections of the enrollment for the presentational layer.
    const currentStatus = enrollment.status;
    const expiryDate = enrollment.invite?.expiry ?? null;
    const regCode = enrollment.invite?.regCode ?? null;

    const isExpired = expiryDate ? expiryDate < new Date() : false;

    // "Revealed" is not separate state: we hold a code exactly when it should be shown. The read
    // (bulkGetMfaStatus) never returns a code, so `regCode` is null on open — the row renders masked
    // with a Reveal button. A freshly generated code (INITIATE/REREGISTER) or an explicit
    // FETCH_REGCODE populates `regCode`, at which point it's shown. There is no state where we hold
    // a code but keep it masked, so a present code == revealed. (Add real state only if a
    // "hide again" / auto-re-mask affordance is ever introduced.)
    const revealed = regCode !== null;

    const applyInitialStatus = (status: AuthgardMfaStatus, expiryMs: number | null) => {
        // The read surfaces status + the real invite expiry but no code (mirrors bulkGetMfaStatus),
        // so an active enrollment opens with its actual expiry and regCode: null — masked, with a
        // Reveal button that fetches the code via FETCH_REGCODE. NO_RECORD / ONBOARDED are terminal
        // (no invite); expired-ness is derived from the expiry date, never fabricated or stored.
        if (status === AuthgardMfaStatus.NO_RECORD || status === AuthgardMfaStatus.ONBOARDED) {
            setEnrollment({ status, invite: null });
        } else {
            setEnrollment({ status, invite: { expiry: expiryMs !== null ? new Date(expiryMs) : null, regCode: null } });
        }
    };

    const resetState = () => {
        // Invalidate any in-flight refresh so its result can't clobber the state we re-seed on reopen.
        refreshIdRef.current += 1;
        applyInitialStatus(initialStatus, initialExpiryMs);
        setErrorMessage(null);
        setSubmitting(false);
        submittingRef.current = false;
        setRefreshing(false);
    };

    // Initialize modal state from the principal's status + real invite expiry when opened.
    useEffect(() => {
        if (open) {
            applyInitialStatus(initialStatus, initialExpiryMs);
        }
    }, [open, initialStatus, initialExpiryMs]);

    // Applies a successful admin-op result to local state. The backend returns the authoritative
    // new status, invite expiry, and (when one is issued) registration code, so we mirror those
    // rather than guess. A result with no userStatus (FETCH_REGCODE) only reveals the code.
    const applyAdminResult = (success: { regCode?: string | null; userStatus?: string | null; mfaExpiry?: string | null }) => {
        if (success.userStatus) {
            const status = normalizeMfaStatus(success.userStatus);
            if (status === AuthgardMfaStatus.NO_RECORD || status === AuthgardMfaStatus.ONBOARDED) {
                setEnrollment({ status, invite: null });
                return;
            }
            // A freshly issued code (INITIATE/REREGISTER) arrives here and is shown immediately
            // simply by being present in the invite — `revealed` derives from regCode != null.
            // Parse through the same NaN-guard as every other expiry site: a malformed mfaExpiry
            // degrades to null (no expiry) instead of an Invalid Date that never reads as expired.
            const expiryMs = parseExpiryMs(success.mfaExpiry);
            setEnrollment({
                status,
                invite: {
                    expiry: expiryMs !== null ? new Date(expiryMs) : null,
                    regCode: success.regCode ?? null,
                },
            });
            return;
        }
        if (success.regCode) {
            const code = success.regCode;
            // FETCH_REGCODE surfaces the existing code without changing status/expiry; storing it
            // reveals it (regCode != null).
            setEnrollment(prev => prev.invite
                ? { status: prev.status, invite: { expiry: prev.invite.expiry, regCode: code } }
                : prev);
        }
    };

    // Runs one admin operation against this principal and applies the result. Returns true on
    // success, false on any failure (setting the user-facing error). PCS authorizes the operation
    // server-side and re-validates the claimed userType — the UI never gates authorization.
    const runAdminOp = async (operation: AuthgardAdminAction): Promise<boolean> => {
        if (submittingRef.current) {
            return false; // guard against a double submit while a request is in flight
        }
        submittingRef.current = true;
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const resultByArn = await executeAuthgardAdminOperation({
                scope,
                operation,
                credentialType: mfaProvider,
                // userType comes off the principal record as a string; assert to UserType for the target.
                targets: [{ principalArn, userType: userType as UserType }],
            });
            const result = resultByArn.get(principalArn);
            if (!result || result.status === AuthgardResultStatus.UNKNOWN) {
                // No result for this target (unknown outcome) — surface a generic failure.
                setErrorMessage(t('manage-credentials-action-failed'));
                return false;
            }
            if (result.status === AuthgardResultStatus.FAILURE) {
                setErrorMessage(humanizeAdminErrorCode(t, result.errorCode));
                return false;
            }
            applyAdminResult(result.value);
            return true;
        } catch (e) {
            // A transport/GraphQL failure (or a missing result payload) throws out of the provider.
            // The outcome is *unknown* — the write may or may not have executed server-side — so
            // surface a generic retry message and leave local state untouched rather than implying it
            // either succeeded or was a no-op. Log the underlying error so the failure is diagnosable.
            logger.error(`executeAuthgardAdminOperation failed for operation ${operation}`, e as Error);
            setErrorMessage(t('manage-credentials-action-failed'));
            return false;
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    // Single "get a valid code" handler, on the V2 vocabulary. INITIATE covers both the first
    // invite and a re-invite for an in-progress enrollment (Authgard V2 folded V1's REINITIATE into
    // INITIATE — the server detects a re-invite itself). A revoked user (RESET_FIDO2 / REREGISTER)
    // re-enrolls via REREGISTER, which revokes and issues a fresh re-registration code:
    //   NO_RECORD / INITIATED / REINITIATED  -> INITIATE
    //   RESET_FIDO2 / REREGISTER             -> REREGISTER
    const handleGenerateCode = () => {
        const revoked = currentStatus === AuthgardMfaStatus.RESET_FIDO2
            || currentStatus === AuthgardMfaStatus.REREGISTER;
        void runAdminOp(revoked ? AuthgardAdminAction.REREGISTER : AuthgardAdminAction.INITIATE);
    };

    // Reveals a valid code via FETCH_REGCODE. Authgard V2 ROTATES the code on fetch: the response
    // carries a fresh code and any previously issued code is invalidated. The modal warns about
    // this next to the Reveal button (manage-credentials-reveal-rotates).
    const handleRevealCode = () => {
        void runAdminOp(AuthgardAdminAction.FETCH_REGCODE);
    };

    // The confirm/keep toggle now lives in the ConfirmableAction UI component; the hook exposes only
    // the actual mutations. Each returns the runAdminOp result (true on success, false on failure).
    const cancelEnrollment = (): Promise<boolean> => runAdminOp(AuthgardAdminAction.CANCEL_INITIATE);

    const revoke = (): Promise<boolean> => runAdminOp(AuthgardAdminAction.REREGISTER);

    // Pulls this principal's current MFA state from bulkGetMfaStatus and re-seeds the modal from it,
    // so the admin sees the live status after the user enrolls (or an invite lapses) without
    // reopening. Shares submittingRef with the mutations so a refresh and an admin op never overlap.
    const handleRefresh = async (): Promise<void> => {
        if (submittingRef.current) {
            return; // a mutation or another refresh is already in flight
        }
        // Tag this refresh; a result that resolves after a reset/reopen or a newer refresh is dropped
        // rather than applied to a modal that has since moved on to a different principal.
        const requestId = ++refreshIdRef.current;
        submittingRef.current = true;
        setRefreshing(true);
        setErrorMessage(null);
        try {
            const result = (await getMfaStatuses([principalArn])).get(principalArn);
            if (requestId !== refreshIdRef.current) {
                return; // superseded by a reset/reopen or a newer refresh — drop this stale result
            }
            if (!result || result.status !== AuthgardResultStatus.SUCCESS) {
                // Couldn't load this principal's status (failure or unknown) — show the "unavailable"
                // state instead of a fabricated one.
                setEnrollment({ status: AuthgardMfaStatus.UNKNOWN, invite: null });
                return;
            }
            // mfaProvider is the credential type ('SCAN2AUTH' | 'FIDO2'), so match on it directly.
            const credential = result.value.find(c => c.credentialType === mfaProvider);
            if (!credential) {
                setEnrollment({ status: AuthgardMfaStatus.NO_RECORD, invite: null });
            } else {
                applyInitialStatus(normalizeMfaStatus(credential.mfaStatus), parseExpiryMs(credential.mfaInviteExpiry));
            }
        } catch (e) {
            // The whole request failed — surface "unavailable" rather than leaving a stale or guessed
            // status behind, unless this refresh was already superseded. Log so it's diagnosable.
            logger.error('getMfaStatuses failed while refreshing MFA status', e as Error);
            if (requestId === refreshIdRef.current) {
                setEnrollment({ status: AuthgardMfaStatus.UNKNOWN, invite: null });
            }
        } finally {
            // Only clear the in-flight flags if we're still the current refresh; a superseding reset
            // has already cleared them (and may have started a newer operation we must not disturb).
            if (requestId === refreshIdRef.current) {
                submittingRef.current = false;
                setRefreshing(false);
            }
        }
    };

    // An active, non-expired enrollment (has a live invite). The modal now derives the reg-code
    // reveal affordance per state (from each state's own expiry) rather than reading a hook flag.
    const hasActiveEnrollment = enrollment.invite != null && !isExpired;

    return {
        currentStatus,
        expiryDate,
        regCode,
        revealed,
        errorMessage,
        submitting,
        refreshing,
        hasActiveEnrollment,
        handleGenerateCode,
        handleRevealCode,
        cancelEnrollment,
        revoke,
        handleRefresh,
        resetState,
    };
};
